import React, { useContext, useMemo } from "react";
import {
  Box,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { ContentContext } from "./ContentProvider";
import { buildTreeItems } from "./buildTreeItems";
import { useNavigate } from "react-router-dom";

function renderTreeItems(node) {
  return {
    id: node.id,
    label: node.label,
    children: (node.children || []).map(renderTreeItems),
  };
}

export default function ContentDrawer({ mobileOpen, onClose }) {
  const { index, query, setQuery, tagFilter, setTagFilter, allTags, filteredFiles } =
    useContext(ContentContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const filteredSet = useMemo(() => new Set(filteredFiles.map((f) => f.path)), [filteredFiles]);

  const treeModel = useMemo(() => {
    const root = index?.root;
    if (!root) return null;

    const rich = buildTreeItems(root, filteredSet);

    // RichTreeView recebe items em formato próprio
    return [renderTreeItems(rich)];
  }, [index, filteredSet]);

  const drawerWidth = 360;

  const drawerContent = (
    <Box sx={{ p: 2, width: drawerWidth }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Navegação
      </Typography>

      <TextField
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        label="Buscar (nome/caminho/título)"
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Categoria</InputLabel>
        <Select value={tagFilter} label="Categoria" onChange={(e) => setTagFilter(e.target.value)}>
          {allTags.map((t) => (
            <MenuItem key={t} value={t}>
              {t === "ALL" ? "Todas" : t}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ mb: 2 }} />

      {!treeModel ? (
        <Typography variant="body2" color="text.secondary">
          Carregando índice...
        </Typography>
      ) : (
        <RichTreeView
          items={treeModel}
          onItemClick={(e, itemId) => {
            if (String(itemId).startsWith("file:")) {
              const encoded = String(itemId).slice("file:".length);
              navigate(`/doc/${encoded}`);
              if (isMobile) onClose?.();
            }
          }}
        />
      )}

      <Divider sx={{ mt: 2 }} />

      <Typography variant="caption" color="text.secondary">
        {index?.generatedAt ? `Index: ${new Date(index.generatedAt).toLocaleString()}` : "Index não carregado"}
      </Typography>
    </Box>
  );

  return (
    <Drawer
      // Se for mobile usa temporary, se não, usa permanent
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth, // Define a largura aqui também
        flexShrink: 0,      // Garante que o drawer não encolha
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box"
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
